import { Locale } from '@enmon/commons';
import {
  DEFAULT_LOCALE,
  SHARED_TRANSLATION_MESSAGES,
  SUPPORTED_LOCALES,
  type TranslationDictionary,
} from '@enmon-monorepo/i18n';
import { createIntl, createIntlCache, type IntlShape, type PrimitiveType } from 'react-intl';
import csMessages from '@/lang/cs.json';
import enMessages from '@/lang/en.json';
import plMessages from '@/lang/pl.json';
import skMessages from '@/lang/sk.json';

const STORAGE_KEY = 'enmon-inventory-locale';

const messagesByLocale: Record<Locale, TranslationDictionary> = {
  [Locale.CS]: { ...SHARED_TRANSLATION_MESSAGES[Locale.CS], ...csMessages },
  [Locale.EN]: { ...SHARED_TRANSLATION_MESSAGES[Locale.EN], ...enMessages },
  [Locale.PL]: { ...SHARED_TRANSLATION_MESSAGES[Locale.PL], ...plMessages },
  [Locale.SK]: { ...SHARED_TRANSLATION_MESSAGES[Locale.SK], ...skMessages },
};

const intlCache = createIntlCache();
let activeIntl: IntlShape | null = null;

/**
 * Captures the live `IntlShape` from the React provider in a single place so the
 * imperative helpers below can translate using the same locale/messages as the
 * React tree. The captured instance is threaded explicitly into the helpers.
 */
export function registerInventoryIntl(intl: IntlShape) {
  activeIntl = intl;
}

export function setInventoryLocale(locale: Locale) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }
}

export function getInventoryLocale(): Locale {
  return readStoredLocale() ?? readNavigatorLocale() ?? DEFAULT_LOCALE;
}

export function getInventoryMessages(locale: Locale): TranslationDictionary {
  return messagesByLocale[locale];
}

export function translateInventoryText(key: string, fallback: string, values: Record<string, PrimitiveType> = {}) {
  return formatInventoryText(activeIntl, key, fallback, values);
}

export function translateFirstInventoryText(
  keys: string[],
  fallback: string,
  values: Record<string, PrimitiveType> = {},
) {
  const locale = getInventoryLocale();
  const key = keys.find(
    candidate => messagesByLocale[locale][candidate] ?? messagesByLocale[DEFAULT_LOCALE][candidate],
  );

  return key ? formatInventoryText(activeIntl, key, fallback, values) : fallback;
}

function formatInventoryText(
  intl: IntlShape | null,
  key: string,
  fallback: string,
  values: Record<string, PrimitiveType>,
) {
  return resolveIntl(intl).formatMessage({ defaultMessage: fallback, id: key }, values);
}

/**
 * Uses the live provider intl when mounted, otherwise builds a standalone intl
 * for the current locale so the imperative helpers keep working outside React
 * (e.g. in unit tests or pure domain code).
 */
function resolveIntl(intl: IntlShape | null): IntlShape {
  if (intl) {
    return intl;
  }

  const locale = getInventoryLocale();

  return createIntl({ defaultLocale: DEFAULT_LOCALE, locale, messages: messagesByLocale[locale] }, intlCache);
}

function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return readInventoryLocale(window.localStorage.getItem(STORAGE_KEY));
}

function readNavigatorLocale(): Locale | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  return readInventoryLocale(navigator.language);
}

function readInventoryLocale(value: string | null | undefined): Locale | null {
  const normalizedLocale = value?.split('-')[0]?.toLowerCase();

  return SUPPORTED_LOCALES.find((locale: Locale) => locale === normalizedLocale) ?? null;
}
